import bankAccountSetupDialogTemplate from './BankAccountSetupDialog.html';
import './BankAccountSetupDialog.scss';

/*******************************************************************************
 * BankAccountSetupDialogService */
/**
 * A modal dialog for add/change of bank account information. User must provide
 * the last statement date/balance, balance as of the books opening date, and any
 * outstanding checks and deposits. Account info can be saved only when the
 * balance difference is accounted for.
 *******************************************************************************/
/* @ngInject */ export function BankAccountSetupDialogService($injector) {
  this.show = $injector.invoke(show);
}

/*******************************************************************************
 * show */
/**
 * Shows the dialog.
 *
 * @param  acctInfo  Bank account information.
 *******************************************************************************/
/* @ngInject */ function show($uibModal, $rootScope) {
  return function (acctInfo) {
    return $uibModal.open({
      template: bankAccountSetupDialogTemplate,
      backdrop: 'static',
      windowClass: 'bank-account-setup-dialog',
      controller: controller,
      controllerAs: '$ctrl',
      bindToController: true,
      scope: Object.assign($rootScope.$new(true), { acctInfo: acctInfo })
    }).result;
  };
}

/*******************************************************************************
 * controller */
/**
 * Controller for the bank account setup dialog.
 *******************************************************************************/
/* @ngInject */ function controller(
  $injector,
  $scope,
  $uibModalInstance,
  ArrayCollection
) {
  var $svcs = {
    $ctrl: this,
    $scope: $scope,
    $uibModalInstance: $uibModalInstance
  };
  var $ctrl = $injector.invoke(
    init$ctrl,
    null,
    Object.assign($svcs, { $svcs: $svcs })
  );

  /*------------------------------------------------------------------*/
  /* Add listener to calculate totals when statement balance changes. */
  /*------------------------------------------------------------------*/
  $scope.$on(
    'destroy',
    $scope.$watch('$ctrl.acctInfo.lastStmtBalance', $ctrl.calculateTotals)
  );

  /*----------------*/
  /* load datagrid. */
  /*----------------*/
  $ctrl.grid = $injector.invoke(buildGrid, null, $svcs);
  $ctrl.grid.data = new ArrayCollection($ctrl.acctInfo.pendingItems);

  /*------------------------------------------------------*/
  /* Set `$ctrl.modified` when user changes account info. */
  /*------------------------------------------------------*/
  var unwatch = $scope.$watch(
    '$ctrl.acctInfo',
    function (nv, ov) {
      if (nv !== ov) unwatch(($ctrl.modified = true));
    },
    true
  );

  $scope.$on('destroy', unwatch);
}

/*******************************************************************************
 * init$ctrl */
/**
 * $ctrl property initializer.
 *******************************************************************************/
/* @ngInject */ function init$ctrl(
  $svcs,
  $ctrl,
  $injector,
  AppConfig,
  AppData,
  AppTools,
  HelpDialog
) {
  /*------------*/
  /* Properties */
  /*------------*/
  $ctrl.modified = false; // Set when `$ctrl.acctInfo` is changed by user.
  $ctrl.openDate = AppData.orgInfo.openDate; // Books open date.
  $ctrl.validators = {}; // `csm-validators` control.

  /*----------*/
  /* Handlers */
  /*----------*/
  $ctrl.addChangeItem = $injector.invoke(addChangeItem, null, $svcs);
  $ctrl.calculateTotals = $injector.invoke(calculateTotals, null, $svcs);
  $ctrl.cancel = $injector.invoke(cancel, null, $svcs);
  $ctrl.importPendingItems = $injector.invoke(importPendingItems, null, $svcs);
  $ctrl.removeItem = $injector.invoke(removeItem, null, $svcs);
  $ctrl.save = $injector.invoke(save, null, $svcs);
  $ctrl.showHelp = function () {
    HelpDialog.show(AppConfig.helpPages.BankAccountSetup);
  };

  /*------------*/
  /* Validators */
  /*------------*/
  $ctrl.accountNameValidator = $injector.invoke(
    AppTools.accountNameValidatorFactory,
    null,
    Object.assign({}, $svcs, { $oldValue: $ctrl.acctInfo.accountName })
  );
  $ctrl.accountNumberValidator = $injector.invoke(
    AppTools.accountNumberValidatorFactory,
    null,
    Object.assign({}, $svcs, { $oldValue: $ctrl.acctInfo.accountNumber })
  );

  /*-----------*/
  /* Accessors */
  /*-----------*/
  return Object.defineProperties($ctrl, {
    /*------------------------------------*/
    /* enableSave - `true` when acct info */
    /* has been modified and balanced.    */
    /*------------------------------------*/
    enableSave: {
      get: function () {
        return $ctrl.modified && $ctrl.isBalanced;
      }
    },

    /*-------------------------------------*/
    /* isBalanced - `true` when calculated */
    /* balance == current account balance. */
    /*-------------------------------------*/
    isBalanced: {
      get: function () {
        return $ctrl.acctInfo.currentBalance === $ctrl.calculatedBalance;
      }
    },

    /*------------------------------------------*/
    /* selectedRow - Current row being edited   */
    /* in grid. `undefined` if grid not active. */
    /*------------------------------------------*/
    selectedRow: {
      get: function () {
        if ($ctrl.grid && $ctrl.grid.api && $ctrl.grid.api.selection) {
          return $ctrl.grid.api.selection.getSelectedGridRows()[0];
        }
        return undefined; // Return undefined if grid.api is not ready
      }
    }
  });
}

/*******************************************************************************
 * addChangeItem */
/**
 * Shows `PendingItemDialog` for entry/edit of outstanding check/deposit.
 *
 * @param  isNew  `true`=Add new item, `false`=Change selectedRow item.
 *******************************************************************************/
/* @ngInject */ function addChangeItem($ctrl, $scope, Data, PendingItemDialog) {
  return function (isNew) {
    var row = isNew ? null : $ctrl.selectedRow;
    var item = isNew ? null : row.entity;
    var data = new Data.OutstandingBankItem(item);

    PendingItemDialog.show(data, isNew).then(function (newItem) {
      if (isNew) $ctrl.grid.data.addItem(newItem);
      else $ctrl.grid.data.replaceItem(item, newItem);
    });
  };
}

/*******************************************************************************
 * buildGrid */
/**
 * Returns grid definition. cells are not editable and row can be selected by
 * clicking. Double-click brings row up in item dialog.
 *
 * TODO: There is currently no way to navigate into the grid and select a row
 * TODO: with the keyboard. `cellNav` does it but requires navigating each cell.
 *******************************************************************************/
/* @ngInject */ function buildGrid(
  $ctrl,
  $scope,
  $templateCache,
  uiGridConstants
) {
  /*------------------------------*/
  /* Configure receipt item grid. */
  /*------------------------------*/
  var gridOptions = {
    columnDefs: [],
    enableColumnMenus: false,
    enableSorting: false,
    enableHorizontalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    minRowsToShow: 5,
    enableRowSelection: true,
    enableRowHeaderSelection: false,
    multiSelect: false,

    /*---------------------------------------------------*/
    /* `registerAPI` adds a method to `apiRegistrations` */
    /* and `onRegisterApi` executes them.                */
    /*---------------------------------------------------*/
    apiRegistrations: [],
    registerAPI: function (f) {
      this.apiRegistrations.push(f);
      return true;
    },
    onRegisterApi: function (api) {
      this.api = api;
      this.apiRegistrations.forEach(function (f) {
        f(api);
      });
    }
  };

  /*------------------------------------------*/
  /* Add double-click handler to row          */
  /* template for changing the selected item. */
  /*------------------------------------------*/
  ((gridOptions.rowTemplate = $templateCache
    .get('ui-grid/ui-grid-row')
    .replace(/^<div/, '<div ng-dblclick="grid.api.onDblClick(row)"')),
    gridOptions.registerAPI(function (api) {
      api.onDblClick = function (row) {
        api.selection.selectRow(row.entity);
        $ctrl.addChangeItem(false);
      };
    }));

  /*---------------------------------------------------*/
  /* Add listener to calculate totals when items are   */
  /* added or removed and when an item cell is edited. */
  /*---------------------------------------------------*/
  gridOptions.registerAPI(function (api) {
    $scope.$on(
      'destroy',
      api.grid.registerDataChangeCallback($ctrl.calculateTotals, [
        uiGridConstants.dataChange.ROW,
        uiGridConstants.dataChange.EDIT
      ])
    );
  });

  /*-----------------*/
  /* Create columns. */
  /*-----------------*/
  gridOptions.columnDefs.push({
    displayName: 'Date',
    name: 'txDate',
    type: 'date',
    cellFilter: 'date:"MM/dd/yyyy"',
    width: 100
  });

  gridOptions.columnDefs.push({
    displayName: 'Type',
    name: 'type',
    type: 'string',
    enableCellEdit: false,
    width: 70
  });

  gridOptions.columnDefs.push({
    displayName: 'Number',
    name: 'gridNumber',
    type: 'string',
    width: 90
  });

  gridOptions.columnDefs.push({
    displayName: 'Name/Description',
    name: 'description',
    type: 'string',
    headerCellClass: 'text-center',
    minWidth: 400
  });

  gridOptions.columnDefs.push({
    displayName: 'Amount',
    name: 'change',
    type: 'number',
    cellClass: 'text-right',
    cellFilter: 'number:2',
    width: 100
  });

  return gridOptions;
}

/*******************************************************************************
 * $ctrl.cancel */
/**
 * Cancel button handler. Shows confirm dialog if user changed any data. Closes
 * the dialog if user confirms or if no changes have been made.
 *******************************************************************************/
/* @ngInject */ function cancel($ctrl, $q, $uibModalInstance, Alert) {
  return function () {
    (!$ctrl.modified
      ? $q.resolve()
      : Alert.confirm('Are you sure you want to cancel?', 'Cancel')
    ).then(function () {
      $uibModalInstance.dismiss();
    });
  };
}

/*******************************************************************************
 * $ctrl.calculateTotals */
/**
 * Calculates `pendingItemsTotal` and `calculatedBalance`. Called when grid data
 * or last statement balance is changed.
 *
 * TODO: If last statement balance is NaN, `calculatedBalance` is set to `NaN` to
 * prevent `Ok` button from being enabled. Better solution is to have a better
 * validator for numbers like we had in Flash Player.
 *******************************************************************************/
/* @ngInject */ function calculateTotals($ctrl, Tools) {
  return function () {
    $ctrl.pendingItemsTotal = Tools.roundMoney(
      $ctrl.acctInfo.pendingItems.reduce(function (total, item) {
        return (total += item.change);
      }, 0)
    );

    if (!isNaN($ctrl.acctInfo.lastStmtBalance))
      $ctrl.calculatedBalance = Tools.roundMoney(
        $ctrl.acctInfo.lastStmtBalance + $ctrl.pendingItemsTotal
      );
    else $ctrl.calculatedBalance = NaN;
  };
}

/*******************************************************************************
 * $ctrl.importPendingItems */
/**
 * Properties and save handler for importing of outstanding bank account items.
 *******************************************************************************/
/* @ngInject */ function importPendingItems(
  $ctrl,
  Data,
  LocalDateSerializer,
  SetupServices
) {
  return {
    serviceURL: SetupServices.url(SetupServices.importPendingItems.name),
    columnHeaders: ['Date', 'Number', 'Type', 'Memo', 'Amount'],
    onSave: function ($items) {
      $items.forEach(function (i) {
        $ctrl.grid.data.addItem(
          new Data.OutstandingBankItem(
            LocalDateSerializer.deserializeDatesOnObject(i)
          )
        );
      });
    }
  };
}

/*******************************************************************************
 * $ctrl.removeItem */
/**
 * Removes the current grid row pending item.
 *******************************************************************************/
/* @ngInject */ function removeItem($ctrl, Alert) {
  return function () {
    var item = $ctrl.selectedRow.entity;
    var type = item.isOther ? 'Item' : item.type;

    Alert.confirm(
      'Are you sure you want to\nRemove this {0}?'.format(type),
      'Remove {0}'.format(type)
    ).then(function () {
      $ctrl.grid.data.removeItem(item);
    });
  };
}

/*******************************************************************************
 * $ctrl.save */
/**
 * Validates input and closes the dialog.
 *******************************************************************************/
/* @ngInject */ function save($ctrl, $uibModalInstance) {
  return function () {
    $ctrl.validators.reset();
    if ($ctrl.validators.validate(true))
      $uibModalInstance.close($ctrl.acctInfo);
  };
}
